import React from 'react';
import Link from 'next/link';
import Head from 'next/head';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EssayGradingTable from '../components/EssayGradingTable';
import { InferGetStaticPropsType } from "next";
import { server } from '../config/index'

const essayBandMapping: { [key: string]: string } = {
    '645fe6da6e2282f143220972': '4',
    '645fe8776e2282f143220977': '5',
    '645feabc6e2282f143220981': '6',
    '645fe9726e2282f14322097c': '8',
    '645feb616e2282f143220986': '8.5',
};


export async function getStaticProps() {
    const ids = Object.keys(essayBandMapping);

    const essayData = await Promise.all(ids.map(async (id) => {
        const response = await fetch(`${server}/api/answer/get/${id}`);
        const result = await response.json();
        const essayBand = essayBandMapping[id];
        const aiScore = result.score;

        // Calculate the average difference
        // Make sure to parse the strings to float to perform the calculation
        const avgDifference = Math.abs(parseFloat(essayBand) - parseFloat(aiScore));

        return {
            id,
            ...result,
            essayBand,
            aiScore,
            avgDifference,
        };
    }));

    return {
        props: {
            essayData,
        },
    };
}

function AccuracyPage({ essayData }: InferGetStaticPropsType<typeof getStaticProps>) {
    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-white">
            <Head>
                <title>IELTS Essay Grading Accuracy & Quality - UpScore.ai vs. Human</title>
                <meta name="description" content="Explore the accuracy and quality of UpScore.ai for grading IELTS essays. See how we benchmark against official IELTS grading and provide consistent and reliable results for IELTS preparation." />
            </Head>

            <Header />

            <main className="my-12 max-w-4xl prose px-2">
                <h1 className="text-3xl font-semibold mb-6 px-1">How Accurate is UpScore.ai for IELTS Essay Grading?</h1>

                <img src="/benchmark.svg" alt="Chart that shows reliability of IELTS writing task 2 scoring using UpScore.ai" />

                <h2 className="text-2xl font-semibold mb-4 px-1">Introduction</h2>
                <p>
                    Can AI help with IELTS? The question of whether AI can assist with IELTS is frequently raised on platforms such as Reddit and YouTube. We firmly believe that AI can be a valuable tool for IELTS preparation, provided it is used correctly.
                </p>
                <p>
                    At UpScore.ai, we offer an AI-based approach to traditional services commonly known as "IELTS essay correction service" or "IELTS writing grading service." Our tool offers automatic, fast, and detailed feedback on IELTS essays, providing an accessible alternative to human evaluators.
                </p>

                <h2 className="text-2xl font-semibold mb-4 px-1">The UpScore.ai Tool Explained</h2>
                <p>
                    Our AI tool uses a combination of Natural Language Processing (NLP) and Machine Learning (ML) techniques to evaluate IELTS essays. It doesn't just look at grammar and vocabulary but also considers more complex elements such as coherence, cohesion, task achievement, and lexical resource—criteria that are part of the official IELTS Writing Task 2 evaluation.
                </p>
                <p>
                    We acknowledge the skepticism surrounding AI's grading capabilities. After all, grading involves a nuanced understanding of language that goes beyond mere syntax and grammar. To address these concerns, we conducted a benchmarking exercise comparing our tool to official IELTS grades.
                </p>

                <h2 className="text-2xl font-semibold mb-4 px-1">Benchmarked Against Official Grading</h2>
                <p>
                    For this purpose, we utilized a range of sample essays from bands 4 to 9, which are available on the ielts.org website. We evaluated each essay using UpScore.ai and compared the results to the evaluations provided by professional examiners from IELTS, which were included alongside the sample essays.
                </p>

                <EssayGradingTable caption="Scores given by UpScore.ai vs official scores" essayData={essayData} />


                <h2 className="text-2xl font-semibold mb-4 px-1">Natural Variation in IELTS Grading</h2>
                <p>
                    It is important to remember that no grading process, whether conducted by humans or AI, can achieve perfect precision. Even human examiners may differ by a range of 0.5-1 band, and occasionally even by 1.5 bands, in their evaluations. While UpScore.ai's performance may not match the precision of a human examiner in every instance, it generally aligns closely with these variations. In the vast majority of cases, the results from UpScore.ai fall within the same grading range. This demonstrates that UpScore.ai is not only a viable option but also a practical and efficient tool for IELTS preparation, offering reliable feedback with an unmatched turnaround time.
                </p>

                <h2 className="text-2xl font-semibold mb-4 px-1">Conclusion: AI as a Supportive Tool</h2>
                <p>
                    While AI tools like UpScore.ai cannot replace the nuanced understanding of a seasoned human grader, they do provide valuable benefits, including immediate feedback, accessibility, and cost-effectiveness. Moreover, these tools can offer valuable practice and insights for IELTS test-takers, complementing their preparation efforts.
                </p>
                <p>
                    At UpScore.ai, we prioritize transparency and believe that it is essential to build trust with our users. We want our users to understand both the strengths and potential limitations of our AI tool. Therefore, we encourage our users to take multiple tests for better reliability and to use our tool as part of a balanced study plan.
                </p>
                <p>
                    If you are ready to experience the benefits of UpScore.ai, we invite you to <Link legacyBehavior href="/trainer"><a>try our demo now</a></Link>. By leveraging the power of AI in your IELTS preparation, you can enhance your writing skills and increase your chances of success. We are here to support you on your journey to achieving your desired IELTS score.
                </p>
            </main>

            <Footer />
        </div>
    );
}

export default AccuracyPage;
